import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { authMiddleware } from "~/lib/auth-middleware";
import { dbMiddleware } from "~/lib/db-middleware";
import {
  getActiveSummit,
  getSummitWithDays,
  getSessionWithSummit,
  getUserSummitRegistration,
  getUserSessionRegistrations,
  getUserWorkshopRegistrationsCount,
  registerForSession as registerForSessionDb,
  unregisterFromSession as unregisterFromSessionDb,
  transformSummitData,
} from "~/lib/server/summit";
import type { SessionFooterVariant } from "~/lib/shared/types/summit";
import { z } from "zod";
import {
  SUMMIT_WORKSHOP_REGISTRATION_LIMIT,
  SUMMIT_VIP_UPSELL_URL,
} from "~/lib/shared/constants/summit";

function buildVipUpsellUrl({
  baseUrl,
  fullName,
  email,
}: {
  baseUrl: string;
  fullName: string | null;
  email: string | null;
}): string {
  const searchParams = new URLSearchParams();

  if (fullName) {
    searchParams.set("full_name", fullName);
  }

  if (email) {
    searchParams.set("email", email);
  }

  if (searchParams.size === 0) {
    return baseUrl;
  }

  return `${baseUrl}?${searchParams.toString()}`;
}

// Get active summit with all data
export const getActiveSummitData = createServerFn({ method: "GET" })
  .middleware([dbMiddleware, authMiddleware])
  .handler(async ({ context }) => {
    const activeSummit = await getActiveSummit();
    if (!activeSummit) {
      throw new Error("No active summit found");
    }

    const summitData = await getSummitWithDays(activeSummit.id);
    if (!summitData) {
      throw new Error("Summit data not found");
    }

    const userRegistration = await getUserSummitRegistration(
      context.user.id,
      activeSummit.id,
    );

    const fullName =
      [context.user.firstName, context.user.lastName]
        .filter((part): part is string => Boolean(part && part.trim()))
        .join(" ") || null;
    const email = context.user.email ?? null;
    const vipUpsellUrl = buildVipUpsellUrl({
      baseUrl: SUMMIT_VIP_UPSELL_URL,
      fullName,
      email,
    });

    // Get all session IDs
    const sessionIds = summitData.days.flatMap((day) =>
      day.sessions.map((session) => session.id),
    );

    const sessionRegistrations = await getUserSessionRegistrations(
      context.user.id,
      sessionIds,
    );

    const registeredSessionIds = new Set(
      Array.from(sessionRegistrations.entries())
        .filter(([, isRegistered]) => isRegistered)
        .map(([sessionId]) => sessionId),
    );

    const workshopRegistrationsCount = summitData.days.reduce((count, day) => {
      return (
        count +
        day.sessions.filter(
          (session) =>
            session.sessionFormat === "workshop" &&
            registeredSessionIds.has(session.id),
        ).length
      );
    }, 0);

    // Transform to frontend format
    const days = transformSummitData(summitData.summit, summitData.days);

    // Create footer variants for each session
    const footerVariants = new Map<string, SessionFooterVariant>();

    summitData.days.forEach((day) => {
      day.sessions.forEach((session) => {
        if (session.recordingUrl) {
          footerVariants.set(session.id, {
            type: "viewing-recording",
            recordingUrl: session.recordingUrl,
            isVipOnly: session.isVipOnly,
          });
        } else {
          const isRegistered = sessionRegistrations.get(session.id) ?? false;
          const isWorkshop = session.sessionFormat === "workshop";
          const requiresVip = Boolean(session.isVipOnly) || isWorkshop;

          if (requiresVip && !userRegistration.isVip) {
            footerVariants.set(session.id, {
              type: "vip-upsell",
              upsellUrl: vipUpsellUrl,
            });
            return;
          }

          const limitReached =
            isWorkshop &&
            !isRegistered &&
            workshopRegistrationsCount >= SUMMIT_WORKSHOP_REGISTRATION_LIMIT;

          footerVariants.set(session.id, {
            type: "registration",
            isRegistered,
            registrationUrl: `/summit/sessions/${session.id}/register`,
            meetingUrl: isRegistered
              ? (session.liveUrl ?? undefined)
              : undefined,
            requiresVip,
            limitReached,
            limit: isWorkshop ? SUMMIT_WORKSHOP_REGISTRATION_LIMIT : undefined,
            registeredCount: isWorkshop
              ? workshopRegistrationsCount
              : undefined,
          });
        }
      });
    });

    return {
      summit: summitData.summit,
      days,
      userRegistration: {
        ...userRegistration,
        workshopRegistrationsCount,
        workshopRegistrationLimit: SUMMIT_WORKSHOP_REGISTRATION_LIMIT,
      },
      footerVariants: Object.fromEntries(footerVariants),
      vipUpsellUrl,
    };
  });

export const getActiveSummitDataOptions = queryOptions({
  queryKey: ["summit", "active"],
  queryFn: () => getActiveSummitData(),
  staleTime: 5 * 60 * 1000, // 5 minutes
});

// Get session data for public session page
export const getSessionData = createServerFn({ method: "GET" })
  .middleware([dbMiddleware])
  .validator(
    z.object({
      sessionId: z.string().uuid(),
    }),
  )
  .handler(async ({ data }) => {
    const sessionData = await getSessionWithSummit(data.sessionId);
    if (!sessionData) {
      throw new Error("Session not found");
    }

    // Transform session to frontend format
    const session = {
      id: sessionData.session.id,
      title: sessionData.session.title,
      description: sessionData.session.description,
      fullDescription: sessionData.session.fullDescription ?? undefined,
      speaker: {
        id: sessionData.session.speaker.id,
        name: sessionData.session.speaker.name,
        title: sessionData.session.speaker.title,
        company: undefined,
        avatarUrl: sessionData.session.speaker.avatarUrl,
        bio: sessionData.session.speaker.bio ?? undefined,
        linkedInUrl: sessionData.session.speaker.linkedInUrl ?? undefined,
        twitterUrl: sessionData.session.speaker.twitterUrl ?? undefined,
        websiteUrl: sessionData.session.speaker.websiteUrl ?? undefined,
      },
      date: sessionData.session.date,
      startTime: sessionData.session.startTime,
      endTime: sessionData.session.endTime,
      timezone: "PST",
      topics: sessionData.session.topics,
      isVipOnly: sessionData.session.isVipOnly,
      recordingUrl: sessionData.session.recordingUrl ?? undefined,
      sessionFormat: sessionData.session.sessionFormat,
    };

    return {
      session,
      summit: sessionData.summit,
    };
  });

export const getSessionDataOptions = (sessionId: string) =>
  queryOptions({
    queryKey: ["session", sessionId],
    queryFn: () => getSessionData({ data: { sessionId } }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

export const getSessionRegistrationState = createServerFn({ method: "GET" })
  .middleware([dbMiddleware, authMiddleware])
  .validator(
    z.object({
      sessionId: z.string().uuid(),
    }),
  )
  .handler(async ({ data, context }) => {
    const sessionData = await getSessionWithSummit(data.sessionId);
    if (!sessionData) {
      throw new Error("Session not found");
    }

    const [
      summitRegistration,
      sessionRegistrations,
      workshopRegistrationsCount,
    ] = await Promise.all([
      getUserSummitRegistration(context.user.id, sessionData.summit.id),
      getUserSessionRegistrations(context.user.id, [sessionData.session.id]),
      getUserWorkshopRegistrationsCount(context.user.id, sessionData.summit.id),
    ]);

    const isWorkshop = sessionData.session.sessionFormat === "workshop";
    const isSessionRegistered =
      sessionRegistrations.get(sessionData.session.id) ?? false;
    const requiresVip = Boolean(sessionData.session.isVipOnly) || isWorkshop;
    const limitReached =
      isWorkshop &&
      !isSessionRegistered &&
      workshopRegistrationsCount >= SUMMIT_WORKSHOP_REGISTRATION_LIMIT;

    return {
      summit: {
        id: sessionData.summit.id,
        title: sessionData.summit.title,
      },
      session: {
        id: sessionData.session.id,
        title: sessionData.session.title,
        sessionFormat: sessionData.session.sessionFormat,
        isVipOnly: sessionData.session.isVipOnly ?? false,
        meetingUrl: sessionData.session.liveUrl ?? null,
      },
      registration: {
        summit: {
          isRegistered: summitRegistration.isRegistered,
          isVip: summitRegistration.isVip,
        },
        session: {
          isRegistered: isSessionRegistered,
        },
        requiresVip,
        isWorkshop,
        limitReached,
        workshopRegistrationsCount,
        workshopRegistrationLimit: SUMMIT_WORKSHOP_REGISTRATION_LIMIT,
      },
    };
  });

export const getSessionRegistrationStateOptions = (sessionId: string) =>
  queryOptions({
    queryKey: ["session", sessionId, "registration-state"],
    queryFn: () => getSessionRegistrationState({ data: { sessionId } }),
    staleTime: 30 * 1000,
  });

// Register for a session
export const registerForSession = createServerFn({ method: "POST" })
  .middleware([dbMiddleware, authMiddleware])
  .validator(
    z.object({
      sessionId: z.string().uuid(),
    }),
  )
  .handler(async ({ data, context }) => {
    await registerForSessionDb(context.user.id, data.sessionId);
    return { success: true };
  });

// Unregister from a session
export const unregisterFromSession = createServerFn({ method: "POST" })
  .middleware([dbMiddleware, authMiddleware])
  .validator(
    z.object({
      sessionId: z.string().uuid(),
    }),
  )
  .handler(async ({ data, context }) => {
    await unregisterFromSessionDb(context.user.id, data.sessionId);
    return { success: true };
  });
